import { TestCaseInCycle } from '@qality/common-model/src/TestCaseInCycle';
import {
  AuxiliaryComparator,
  Comparator,
} from '@qality/common-model/src/sorting/Comparator';
import { SortStrategyWithNone } from '@qality/common-model/src/sorting/Sort';
import ComparatorUtils from '@qality/common-utils/src/ComparartorUtils';

export type TestCycleSortFunction = Comparator<TestCaseInCycle>;

export type TestCycleAuxiliarySortFunction =
  AuxiliaryComparator<TestCaseInCycle>;

export const sortIdAsc: TestCycleSortFunction = (a, b) => {
  const { id: idA } = a;
  const { id: idB } = b;

  return ComparatorUtils.compareNumbers(idA, idB);
};

export const sortIdAscIfNeeded: TestCycleAuxiliarySortFunction = (
  previousComparatorResult,
  a,
  b
) => {
  return previousComparatorResult === 0
    ? sortIdAsc(a, b)
    : previousComparatorResult;
};

export const sortNone: TestCycleSortFunction = () => {
  return 0;
};

export const sortTypeAsc: TestCycleSortFunction = (a, b) => {
  const typeA = a.testType?.name ? a.testType?.name : '';
  const typeB = b.testType?.name ? b.testType?.name : '';

  const result = ComparatorUtils.compareStrings(typeA, typeB);

  return sortIdAscIfNeeded(result, a, b);
};

export const sortTypeDsc: TestCycleSortFunction = (a, b) => {
  const resultAsc = sortTypeAsc(a, b);

  return ComparatorUtils.reverseComparatorResult(resultAsc);
};

export const sortSummaryAsc: TestCycleSortFunction = (a, b) => {
  const { summary: summaryA } = a.testCase?.fields ?? { summary: '' };
  const { summary: summaryB } = b.testCase?.fields ?? { summary: '' };

  const result = ComparatorUtils.compareStrings(summaryA, summaryB);

  return sortIdAscIfNeeded(result, a, b);
};

export const sortSummaryDsc: TestCycleSortFunction = (a, b) => {
  const resultAsc = sortSummaryAsc(a, b);

  return ComparatorUtils.reverseComparatorResult(resultAsc);
};

export const sortStatusAsc: TestCycleSortFunction = (a, b) => {
  const { name: nameA } = a.statusDetail;
  const { name: nameB } = b.statusDetail;

  const result = ComparatorUtils.compareStrings(nameA, nameB);

  return sortIdAscIfNeeded(result, a, b);
};

export const sortStatusDsc: TestCycleSortFunction = (a, b) => {
  const resultAsc = sortStatusAsc(a, b);

  return ComparatorUtils.reverseComparatorResult(resultAsc);
};

export const sortExecutionAssigneeAsc: TestCycleSortFunction = (a, b) => {
  const executionAssigneeA =
    a.testExecution?.executionAssignee?.getDisplayName() ?? '';
  const executionAssigneeB =
    b.testExecution?.executionAssignee?.getDisplayName() ?? '';

  const result = ComparatorUtils.compareStrings(
    executionAssigneeA,
    executionAssigneeB
  );

  return sortIdAscIfNeeded(result, a, b);
};

export const sortExecutionAssigneeDsc: TestCycleSortFunction = (a, b) => {
  const resultAsc = sortExecutionAssigneeAsc(a, b);

  return ComparatorUtils.reverseComparatorResult(resultAsc);
};

export const sortNumberOfBugsAsc: TestCycleSortFunction = (a, b) => {
  const { length: numberOfBugsA } = a.issuesLinkedToExecution ?? [];
  const { length: numberOfBugsB } = b.issuesLinkedToExecution ?? [];

  const result = ComparatorUtils.compareNumbers(numberOfBugsA, numberOfBugsB);

  return sortIdAscIfNeeded(result, a, b);
};

export const sortNumberOfBugsDsc: TestCycleSortFunction = (a, b) => {
  const resultAsc = sortNumberOfBugsAsc(a, b);

  return ComparatorUtils.reverseComparatorResult(resultAsc);
};

export type TestCycleSortableFields =
  | 'type'
  | 'summary'
  | 'status'
  | 'execution-assignee'
  | 'number-of-bugs';

export type TestCycleSortStrategy =
  SortStrategyWithNone<TestCycleSortableFields>;

export const TestCycleSortFunctions: Record<
  TestCycleSortStrategy,
  TestCycleSortFunction
> = {
  none: sortNone,
  'type-asc': sortTypeAsc,
  'type-dsc': sortTypeDsc,
  'summary-asc': sortSummaryAsc,
  'summary-dsc': sortSummaryDsc,
  'status-asc': sortStatusAsc,
  'status-dsc': sortStatusDsc,
  'execution-assignee-asc': sortExecutionAssigneeAsc,
  'execution-assignee-dsc': sortExecutionAssigneeDsc,
  'number-of-bugs-asc': sortNumberOfBugsAsc,
  'number-of-bugs-dsc': sortNumberOfBugsDsc,
};
